import { z } from 'zod';
import { BookOverride, Role, StaffEngagement } from '../enums.js';
import { Email, IsoDateTime, NonEmptyName, Phone, TimeOfDay, Uuid } from '../primitives.js';

/**
 * A staff member. `renterId` is non-null exactly when this person is a chair renter who
 * owns a sealed book; their clients/appointments carry that renterId and are RLS-isolated.
 * `bookOverride` is meaningful only in `mixed` salons.
 */
export const Staff = z
  .object({
    id: Uuid,
    salonId: Uuid,
    locationId: Uuid.nullable(),
    userId: Uuid.nullable(),
    displayName: NonEmptyName,
    role: Role,
    engagement: StaffEngagement,
    renterId: Uuid.nullable().describe('set when this staff owns a sealed client book'),
    bookOverride: BookOverride,
    email: Email.nullable(),
    phone: Phone.nullable(),
    title: z.string().max(80).nullable().describe('e.g. "Colour specialist"'),
    active: z.boolean(),
    onlineBookable: z
      .boolean()
      .describe('publicly bookable online; default true for employed, false for sealed renters'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Staff' });
export type Staff = z.infer<typeof Staff>;

export const CreateStaff = z
  .object({
    displayName: NonEmptyName,
    role: Role,
    engagement: StaffEngagement.default('employed'),
    locationId: Uuid.nullish(),
    email: Email.nullish(),
    phone: Phone.nullish(),
    title: z.string().max(80).nullish(),
    /** In `mixed` salons, choose how this person's book behaves. Defaults to inherit. */
    bookOverride: BookOverride.default('inherit'),
    /**
     * Whether this person is bookable through the public online flow. Omitted → the server picks a
     * safe default at create time (employed = true, sealed chair renter = false).
     */
    onlineBookable: z.boolean().optional(),
  })
  .meta({ id: 'CreateStaff' });
export type CreateStaff = z.infer<typeof CreateStaff>;

export const UpdateStaff = CreateStaff.partial()
  .extend({ active: z.boolean().optional() })
  .meta({ id: 'UpdateStaff' });
export type UpdateStaff = z.infer<typeof UpdateStaff>;

/** A recurring weekly working block for a staff member (drives availability). */
export const WorkingHours = z
  .object({
    id: Uuid,
    staffId: Uuid,
    weekday: z.int().min(0).max(6).describe('0 = Sunday … 6 = Saturday'),
    startTime: TimeOfDay,
    endTime: TimeOfDay,
  })
  .meta({ id: 'WorkingHours' });
export type WorkingHours = z.infer<typeof WorkingHours>;

export const SetWorkingHours = z
  .object({
    hours: z
      .array(
        z.object({
          weekday: z.int().min(0).max(6),
          startTime: TimeOfDay,
          endTime: TimeOfDay,
        }),
      )
      .max(50),
  })
  .meta({ id: 'SetWorkingHours' });
export type SetWorkingHours = z.infer<typeof SetWorkingHours>;
